import { useMemo } from 'react';
import clsx from 'clsx';
import PhoneIcon from '@mui/icons-material/Phone';

import {
  FormItem,
  Input,
  Form,
  Card,
  SubmitButton,
  Loading,
  AutoComplete,
  Toggle,
  Datepicker,
  Dropdown,
  Tooltip,
} from '../../../components';
import { useConnect } from './useConnect';
import { PermissionCheck } from '../../../components';
import type { SourceType, ConnectFormProps } from './types';

const sourceOptions: OptionType<SourceType>[] = [
  { value: '', label: '\u00A0' },
  { value: 'dev', label: 'DEV' },
  { value: 'uat', label: 'UAT' },
];

export const Connect = () => {
  const {
    onChange,
    form,
    handleCall,
    isCalling,
    presentations,
    agents,
    loading,
  } = useConnect();

  const isDisabled = useMemo(() => {
    if (loading || isCalling) return true;
    if (form?.override_url) return false;
    if (!form?.agent_id && !form?.override_agent_id) return true;

    return !form?.meet_link;
  }, [form, loading, isCalling]);

  return (
    <>
      {isCalling && <Loading />}
      <Form onChange={onChange} initialValue={form as ConnectFormProps}>
        <div
          className={clsx(
            isCalling && 'hidden',
            'flex items-center justify-start min-h-[80vh] px-4 flex-col gap-lg'
          )}
        >
          <Card
            collapsable
            title="General"
            withOverflow={false}
            defaultCollapsed={false}
            contentClassName="flex flex-col gap-y-sm"
            className="w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl xl:max-w-3xl"
          >
            <div className="flex flex-col gap-y-4">
              <FormItem<ConnectFormProps>
                required
                name="meet_link"
                label="Conference Call URL"
                className="w-full"
                inline={false}
              >
                <Input placeholder="Google Meet, Zoom, Teams, WebEx" />
              </FormItem>
              <FormItem<ConnectFormProps>
                label="Agent"
                name="agent_id"
                className="w-full"
                inline={false}
              >
                <Dropdown placeholder="Select Agent" options={agents} />
              </FormItem>
              <FormItem<ConnectFormProps>
                name="presentation_id"
                label="Presentation"
                className="w-full"
                inline={false}
              >
                <AutoComplete
                  options={presentations || []}
                  placeholder="Select Presentation"
                />
              </FormItem>

              <FormItem<ConnectFormProps>
                inline={false}
                name="join_at"
                className="w-full"
                label="Schedule Time"
                tooltip="Leave field empty if NOW"
              >
                <Datepicker minDate={new Date()} />
              </FormItem>

              <FormItem<ConnectFormProps>
                name="bg_info"
                label="Conference Call Background Info"
                className="w-full"
                inline={false}
                tooltip="Make sure to use {ConferenceCallInfo} variable in Agent's prompt so it's aware about this data"
              >
                <Input
                  placeholder="Conference Call Background Info"
                  disabled={isDisabled}
                  multiline
                />
              </FormItem>

              <PermissionCheck action="super_admin">
                <FormItem<ConnectFormProps>
                  inline={false}
                  className="w-full"
                  name="knowledgeBaseId"
                  label="Knowledge Base Id"
                >
                  <Input placeholder="Knowledge Base Id" />
                </FormItem>
                <FormItem<ConnectFormProps>
                  inline={false}
                  name="bg_info_id"
                  label="Bg Info Id"
                  className="w-full"
                >
                  <Input placeholder="Bg Info Id" />
                </FormItem>
              </PermissionCheck>

              <FormItem<ConnectFormProps>
                inline={false}
                className="w-full"
                name="use_video_avatar"
                label="Enable Video Avatar Mode"
              >
                <Toggle />
              </FormItem>

              <div className="flex justify-end w-full">
                <SubmitButton
                  label="Call"
                  size="medium"
                  type="primary"
                  onClick={handleCall}
                  icon={<PhoneIcon className="mr-sm" />}
                />
              </div>
            </div>
          </Card>
          <PermissionCheck action="super_admin">
            <Card
              collapsable
              title="Overrides"
              withOverflow={false}
              tooltip="For advanced users"
              contentClassName="flex flex-col gap-y-sm"
              className="w-full sm:max-w-lg md:max-w-xl lg:max-w-2xl xl:max-w-3xl"
            >
              <FormItem<ConnectFormProps>
                name="override_agent_name"
                className="w-full"
                label="Override Agent Name"
                inline={false}
                tooltip="Display different name for agent that joins call"
              >
                <Input placeholder="Agent Name" />
              </FormItem>
              <FormItem<ConnectFormProps>
                name="override_agent_id"
                className="w-full"
                label="Override Agent ID"
                inline={false}
                tooltip="Add any agent by ID"
              >
                <Input placeholder="Agent ID" />
              </FormItem>
              <FormItem<ConnectFormProps>
                inline={false}
                className="w-full"
                name="override_agent_source"
                label="Override Agent Source"
                tooltip="Select environment where agent is created"
              >
                <Dropdown
                  options={sourceOptions}
                  placeholder="Select Agent Source"
                />
              </FormItem>
              <FormItem<ConnectFormProps>
                inline={false}
                className="w-full"
                name="override_script_source"
                label="Override Script Source"
                tooltip="Select widget version used for making a call"
              >
                <Dropdown
                  options={sourceOptions}
                  placeholder="Select Script Source"
                />
              </FormItem>
              <FormItem<ConnectFormProps>
                name="override_url"
                label="Override URL"
                className="w-full"
                inline={false}
                tooltip="Take full control over URL used to add agent to a call"
              >
                <Input placeholder="Override URL" />
              </FormItem>
            </Card>
          </PermissionCheck>
        </div>
      </Form>
    </>
  );
};
